import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './sidenav-group.scss?inline'

export type Spacing = 'top' | 'bottom' | 'both' | 'none'

/**
 * `dap-ds-sidenav-group`
 * @summary Side navigation group
 * @element dap-ds-sidenav-group
 * @title - Side navigation group
 * @group side-navigation
 *
 * @slot - The content of the side navigation group.
 * @slot submenu - The submenu of the side navigation group.
 * @slot suffix - The suffix of the side navigation group.
 *
 * @csspart base - The main side navigation group container.
 * @csspart title - The title of the side navigation group.
 * @csspart link - The link of the side navigation group.
 * @csspart toggle - The toggle button of the side navigation group.
 * @csspart submenu - The submenu of the side navigation group.
 *
 * @cssproperty --dds-sidenav-group-margin-bottom - Default bottom margin of the sidenav group.
 * @cssproperty --dds-sidenav-group-border-radius - Border radius of the sidenav group.
 * @cssproperty --dds-sidenav-group-spacing-margin - Margin used for spacing variants.
 * @cssproperty --dds-sidenav-group-item-min-height - Minimum height of the sidenav group item.
 * @cssproperty --dds-sidenav-group-item-padding - Padding of the sidenav group item.
 * @cssproperty --dds-sidenav-group-item-color - Text color of the sidenav group item.
 * @cssproperty --dds-sidenav-group-item-font-size - Font size of the sidenav group item.
 * @cssproperty --dds-sidenav-group-item-font-weight - Font weight of the sidenav group item.
 * @cssproperty --dds-sidenav-group-item-bg-active - Background color when the item is active.
 * @cssproperty --dds-sidenav-group-item-bg-hover - Background color on hover and active states.
 * @cssproperty --dds-sidenav-group-toggle-margin - Margin for the toggle button.
 * @cssproperty --dds-sidenav-group-submenu-padding - Padding for the submenu.
 */
@customElement('dap-ds-sidenav-group')
export default class DapDSSideNavGroup extends DdsElement {
  /** The href of the side navigation item */
  @property({ reflect: true }) href: string

  /** Whether the side navigation item is active */
  @property({ type: Boolean, reflect: true }) active = false

  /** The spacing of the side navigation item
   * @type {'top' | 'bottom' | 'both' | 'none'}
   */
  @property({ reflect: true }) spacing: Spacing = 'none'

  /** Whether the side navigation group is open */
  @property({ type: Boolean, reflect: true }) open = false

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <div
        part="base"
        role="listitem"
        class=${clsx(
          'sidenav-group',
          `sidenav-group--spacing-${this.spacing}`,
        )}>
        <div
          class=${clsx('sidenav-group__item', {
            'sidenav-group__item--active': this.active,
          })}>
          <div
            class=${clsx('sidenav-group__title')}
            part="title"
            id="title"
            @click=${() => {
              if (!this.open) {
                this.open = true
              } else if (this.open && !this.href) {
                this.open = false
              }
            }}>
            ${this.href
              ? html`<a
                  href=${this.href}
                  class="sidenav-group__link"
                  part="link">
                  <slot></slot>
                  <slot name="suffix"></slot>
                </a>`
              : html`<slot></slot>`}
          </div>
          <button
            part="toggle"
            class=${clsx('sidenav-group__toggle', {
              'sidenav-group__toggle--open': this.open,
            })}
            aria-labelledby="title"
            @click=${(e: any) => {
              e.preventDefault()
              this.open = !this.open
            }}>
            <dap-ds-icon-arrow-right-s-line
              part="icon"
              aria-hidden="true"></dap-ds-icon-arrow-right-s-line>
          </button>
        </div>
        <ul
          part="submenu"
          role="list"
          class=${clsx('sidenav-group__submenu', {
            'sidenav-group__submenu--open': this.open,
          })}>
          <slot name="submenu"></slot>
        </ul>
      </div>
    `
  }
}
